/**
 * 0.26.0 — per-user AUDIT LOG read model (owner-only). Human-readable labels for the append-only
 * `audit_log` action codes, the query schemas, and the DTOs the owner UI renders. The raw action code
 * travels alongside every label (shown on hover) so nothing is hidden by the translation.
 */
import { z } from 'zod';

/** Human label per audit action code. Unknown codes fall back to a prettified code. */
export const AUDIT_ACTION_LABELS: Record<string, string> = {
  'owner.update': 'Updated owner details',
  'property.create': 'Added a property',
  'property.update': 'Edited a property',
  'property.delete': 'Deleted a property',
  'unit.create': 'Added a unit',
  'unit.update': 'Edited a unit',
  'unit.delete': 'Deleted a unit',
  'appliance.create': 'Added an appliance',
  'appliance.update': 'Edited an appliance',
  'appliance.delete': 'Deleted an appliance',
  'tenant.create': 'Added a tenant',
  'tenant.update': 'Edited a tenant',
  'tenant.delete': 'Removed a tenant',
  'tenant.invite': 'Sent a portal invite',
  'staff.create': 'Created a staff login',
  'staff.update': 'Changed a staff login',
  'lease.create': 'Created a lease',
  'lease.update': 'Edited a lease',
  'lease_tenant.add': 'Added a tenant to a lease',
  'lease_tenant.remove': 'Removed a tenant from a lease',
  'lease.document.upload': 'Uploaded the executed lease',
  'schedule.replace': 'Changed the payment schedule',
  'deadline.register': 'Started a compliance clock',
  'deadline.satisfy': 'Satisfied a compliance clock',
  'deadline.cancel': 'Canceled a compliance clock',
  'deadline.sweep': 'Deadline sweep ran',
  'ledger.post': 'Posted to the ledger',
  'receipt.generate': 'Generated a receipt',
  'work_order.create': 'Opened a work order',
  'work_order.status_change': 'Changed a work order status',
  'work_order.note': 'Added a work order note',
  'work_order.dispatch': 'Dispatched a work order',
  'org.contractor_update': 'Changed the contractor dispatch settings',
  'contractor.create': 'Added a contractor',
  'contractor.update': 'Edited a contractor',
  'contractor.invite': 'Invited a contractor',
  'contractor.deactivate': 'Deactivated a contractor',
  'contractor.activate': 'Reactivated a contractor',
  'contractor.view_tenant_contact': 'Contractor viewed tenant contact details',
  'work_order.contractor_assign': 'Assigned a contractor',
  'work_order.contractor_unassign': 'Unassigned a contractor',
  'work_order.photo_add': 'Added work order photos',
  'work_order_cost.submit': 'Submitted a cost',
  'work_order_cost.update': 'Edited a cost',
  'work_order_cost.withdraw': 'Withdrew a cost',
  'work_order_cost.approve': 'Approved a cost',
  'work_order_cost.reject': 'Rejected a cost',
  'work_order.manual_cost_update': 'Changed a manual work order cost',
  'schedule_proposal.create': 'Proposed an entry window',
  'schedule_proposal.withdraw': 'Withdrew an entry-window proposal',
  'schedule_proposal.confirm': 'Confirmed an entry window',
  'schedule_proposal.decline': 'Declined an entry window',
  'schedule_proposal.supersede': 'Entry-window proposal superseded',
  'entry_notice.serve': 'Served an entry notice',
  'entry_notice.serve_failed': 'Entry notice failed to send',
  'entry_notice.email_sent': 'Entry notice copy emailed to the tenant',
  'entry_notice.email_not_sent': 'Entry notice email copy NOT sent',
  'document.create': 'Added a document',
  'document.delete': 'Deleted a document',
  'document.visibility_update': 'Changed document visibility',
  'move_out.create': 'Started a move-out report',
  'move_out.update': 'Edited a move-out report',
  'move_out.delete': 'Deleted a move-out report',
  'move_out.finalize': 'Finalized a move-out statement',
  'move_out.recipient_add': 'Added a move-out recipient',
  'move_out.recipient_update': 'Edited a move-out recipient',
  'move_out.recipient_remove': 'Removed a move-out recipient',
  'move_out.deduction_add': 'Added a deposit deduction',
  'move_out.deduction_update': 'Edited a deposit deduction',
  'move_out.deduction_remove': 'Removed a deposit deduction',
  'move_out.document_attach': 'Attached a move-out document',
  'move_out.document_update': 'Edited a move-out document',
  'move_out.document_detach': 'Detached a move-out document',
  'move_out.statement_sign': 'Signed the deposit statement',
  'move_out.statement_send': 'Emailed the signed deposit statement',
  'move_out.statement_test_send': 'Sent a test copy of the deposit statement email to the owner',
  'move_out.recipient_delivery_update': 'Changed a statement delivery email',
  'move_out.packet_access': 'Statement download link opened',
  'move_out.packet_test_access': 'Owner opened a TEST statement download link',
  'move_out.reopen': 'Reopened a finalized move-out statement for amendment',
  'org.signature_update': 'Changed the signature name / title',
  'org.signature_image_upload': 'Uploaded a handwritten signature image',
  'thread.create': 'Started a message thread',
  'message.post': 'Sent a message',
  'payment_method.create': 'Added a payment method',
  'payment_method.update': 'Changed a payment method',
  'payment_method.delete': 'Removed a payment method',
  'payment.attempt_create': 'Started an online payment',
  'payment.attempt_reuse': 'Reopened an unfinished online payment',
  'payment.attempt_client_update': 'Browser reported a payment step',
  'payment.attempt_cancel': 'Canceled an online payment',
  'payment.attempt_auto_cancel': 'Auto-canceled an abandoned online payment',
  'payment.attempt_sync': 'Synced online payments from Stripe',
  'payment.attempt_upsert': 'Stripe reported a payment the app had not seen',
  'auth.login': 'Signed in',
  'auth.logout': 'Signed out',
  'mfa.enroll': 'Enrolled two-factor authentication',
  'password.change': 'Changed password',
  'audit.view': 'Viewed the audit log',
  // Non-audit timeline items (per-person view).
  'session.sign_in': 'Session started',
  'payment.event': 'Online payment step',
};

export function auditActionLabel(action: string): string {
  const hit = AUDIT_ACTION_LABELS[action];
  if (hit) return hit;
  const t = action.replace(/[._]/g, ' ').trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : action;
}

/** People whose activity can be opened (`/admin/activity/:kind/:id`). `staff` = an owner/staff login. */
export const AUDIT_PERSON_KINDS = ['tenant', 'staff', 'contractor'] as const;
export type AuditPersonKind = (typeof AUDIT_PERSON_KINDS)[number];

/** Subjects an audit row can be "about" (`subject=<kind>:<id>`). */
export const AUDIT_SUBJECT_KINDS = ['tenant', 'staff', 'contractor', 'lease'] as const;

const idRe = /^[A-Za-z0-9_-]{1,128}$/;
const dateLike = z
  .string()
  .trim()
  .regex(/^\d{4}-\d{2}-\d{2}(T[\d:.]+(Z|[+-]\d{2}:\d{2})?)?$/, { message: 'use yyyy-mm-dd or an ISO timestamp' });

export const auditQuerySchema = z.object({
  actorUserId: z.string().trim().regex(idRe).optional(),
  subject: z
    .string()
    .trim()
    .regex(/^(tenant|staff|contractor|lease):[A-Za-z0-9_-]{1,128}$/, { message: 'subject must be <tenant|staff|contractor|lease>:<id>' })
    .optional(),
  action: z.string().trim().regex(/^[a-z_.]{1,64}$/).optional(),
  from: dateLike.optional(),
  to: dateLike.optional(),
  cursor: z.string().trim().max(512).optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
});
export type AuditQuery = z.infer<typeof auditQuerySchema>;

export const auditPersonQuerySchema = z.object({
  cursor: z.string().trim().max(512).optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
});

/** Displayable detail values — whitelisted keys, primitives only (see apps/api/src/lib/audit-read.ts). */
export type AuditDetailValue = string | number | boolean | null;

export interface AuditEntryDTO {
  /** Stable, unique timeline key (`audit:<id>`, `session:<n>`, `payment:<id>`). */
  key: string;
  at: string;
  source: 'audit' | 'session' | 'payment';
  /** Per-person view: was the person the actor (`by`), the subject (`about`), or both. */
  relation?: 'by' | 'about' | 'both' | 'session' | 'payment';
  action: string;
  label: string;
  actorType: string | null;
  actorUserId: string | null;
  actorLabel: string | null;
  entityType: string | null;
  entityId: string | null;
  /** Whitelisted, primitive-only details (never secrets / raw PII / bank numbers / ciphertext). */
  details: Record<string, AuditDetailValue>;
  /** For edits: the NAMES of the fields that changed (never their values). */
  changedFields: string[];
  /** Truncated (IPv4 /24, IPv6 /48) — never the full address. */
  ipPrefix: string | null;
  /** Summarized (browser + OS), never the raw user-agent string. */
  device: string | null;
}

export interface AuditListResponse {
  items: AuditEntryDTO[];
  nextCursor: string | null;
}

export interface AuditPersonDTO {
  kind: AuditPersonKind;
  id: string;
  name: string;
  userId: string | null;
  role: string | null;
}

export interface AuditPersonResponse extends AuditListResponse {
  person: AuditPersonDTO;
}
